import * as React from "react";
import { useState } from "react";
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  mergeStyles,
  getTheme,
} from "@fluentui/react";
import {
  SnapshotState,
  getSnapshotState,
  freezeSheetyBanksFormulas,
  unfreezeSheetyBanksFormulas,
} from "../../snapshotFunctions";
import { isSignedIn } from "../../../auth/authDialog";

const theme = getTheme();

const statusCardStyle = mergeStyles({
  border: `1px solid ${theme.palette.neutralLight}`,
  borderRadius: "4px",
  padding: "8px 12px",
  backgroundColor: theme.palette.neutralLighterAlt,
});

function describeState(state: SnapshotState | null): string {
  if (!state) return "Workbook is live — no snapshot is active.";
  const sheets: string[] = [];
  for (const entry of state.entries) {
    if (sheets.indexOf(entry.sheet) === -1) sheets.push(entry.sheet);
  }
  const when = new Date(state.createdAt).toLocaleString();
  const formulas = `${state.entries.length} formula${state.entries.length === 1 ? "" : "s"}`;
  const across = `${sheets.length} sheet${sheets.length === 1 ? "" : "s"}`;
  return `${formulas} frozen across ${across} — snapshotted ${when}.`;
}

const Snapshot: React.FunctionComponent = () => {
  const [state, setState] = useState<SnapshotState | null>(() => getSnapshotState());
  const [busy, setBusy] = useState<"freeze" | "unfreeze" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFreeze = async () => {
    setError(null);
    setNote(null);
    setBusy("freeze");
    try {
      const result = await freezeSheetyBanksFormulas();
      if (!result.supported) {
        setError(
          "Snapshot needs a newer Excel (ExcelApi 1.12) — this version can't map spilled ranges safely."
        );
      } else if (result.count === 0) {
        setNote("No live SHEETYBANKS formulas found to snapshot.");
      } else {
        const comments =
          result.commentsFailed > 0
            ? ` (${result.commentsFailed} provenance comment${result.commentsFailed === 1 ? "" : "s"} could not be added)`
            : "";
        setNote(
          `Froze ${result.count} formula${result.count === 1 ? "" : "s"} to static values${comments}.`
        );
      }
    } catch (err) {
      setError(`Snapshot failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setState(getSnapshotState());
      setBusy(null);
    }
  };

  const handleUnfreeze = async () => {
    setError(null);
    setNote(null);
    setBusy("unfreeze");
    try {
      const result = await unfreezeSheetyBanksFormulas();
      const skipped =
        result.skipped > 0
          ? ` ${result.skipped} could not be restored (sheet renamed, deleted, or protected) and stay frozen.`
          : "";
      const auth = !isSignedIn() && result.restored > 0 ? " Sign in to repopulate live values." : "";
      setNote(
        `Restored ${result.restored} formula${result.restored === 1 ? "" : "s"}.${skipped}${auth}`
      );
    } catch (err) {
      setError(`Unfreeze failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setState(getSnapshotState());
      setBusy(null);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
        Snapshot replaces every SHEETYBANKS formula in the workbook with its current values, so the
        numbers are as-of today and won&apos;t move — safe to share with people who don&apos;t have
        the add-in. Each frozen cell gets a comment recording what it was and when it was pulled,
        and Unfreeze restores the live formulas.
      </Text>

      <div className={statusCardStyle}>
        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
          {describeState(state)}
        </Text>
      </div>

      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}
      {note && (
        <MessageBar messageBarType={MessageBarType.info} onDismiss={() => setNote(null)}>
          {note}
        </MessageBar>
      )}
      {busy && (
        <Spinner
          size={SpinnerSize.small}
          label={busy === "freeze" ? "Snapshotting formulas…" : "Restoring formulas…"}
        />
      )}

      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <PrimaryButton
          text="Snapshot to values"
          iconProps={{ iconName: "Camera" }}
          onClick={handleFreeze}
          disabled={busy != null}
        />
        <DefaultButton
          text="Unfreeze"
          iconProps={{ iconName: "Refresh" }}
          onClick={handleUnfreeze}
          disabled={busy != null || state == null}
        />
      </Stack>
    </Stack>
  );
};

export default Snapshot;
